/**
 * The export area selector.
 *
 * Replaces the old screen-space `<div>` overlay. A `google.maps.Rectangle` is
 * anchored to the ground rather than to pixels, so panning and zooming leave
 * the selection where the user put it, the map stays interactive underneath,
 * and drag plus eight resize handles come from the Maps API instead of four
 * hand-rolled edge handlers with hardcoded pixel limits.
 *
 * Constructed on demand from the Export panel, by which point the map exists,
 * so there is no need to wait on the Maps API here.
 */

// $accent-color / $primary-bg from src/style/_vars.scss
const STROKE = '#90ff00'
const FILL = '#90ff00'

/** Bounds literal for a rectangle inset inside the current viewport. */
function defaultBounds(map) {
  const view = map.getBounds()
  if (!view) return null

  const ne = view.getNorthEast()
  const sw = view.getSouthWest()
  const latInset = (ne.lat() - sw.lat()) * 0.2
  const lngInset = (ne.lng() - sw.lng()) * 0.2

  return {
    north: ne.lat() - latInset,
    south: sw.lat() + latInset,
    east: ne.lng() - lngInset,
    west: sw.lng() + lngInset
  }
}

export function createAreaSelector({ map, onChange }) {
  if (!map || typeof google === 'undefined' || !google.maps || !google.maps.Rectangle) {
    return null
  }

  const bounds = defaultBounds(map)
  if (!bounds) return null

  const rectangle = new google.maps.Rectangle({
    map,
    bounds,
    editable: true,
    draggable: true,
    clickable: true,
    strokeColor: STROKE,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: FILL,
    fillOpacity: 0.08,
    zIndex: 20
  })

  // bounds_changed fires continuously through a drag; coalesce to one recount
  // per frame so the panel keeps up without re-walking every object per pixel.
  let frame = null
  const emit = () => {
    frame = null
    const b = rectangle.getBounds()
    if (b && onChange) onChange(b.toJSON())
  }
  const listener = rectangle.addListener('bounds_changed', () => {
    if (frame === null) frame = window.requestAnimationFrame(emit)
  })

  if (onChange) onChange(bounds)

  return {
    getBounds() {
      const b = rectangle.getBounds()
      return b ? b.toJSON() : null
    },
    /**
     * Remove only what this selector created. Never clearInstanceListeners on
     * the map — that wipes every listener on it, including the clusterer's and
     * Google's own (WEB-7076).
     */
    destroy() {
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
        frame = null
      }
      listener.remove()
      rectangle.setMap(null)
    }
  }
}

export default createAreaSelector
